import { useState } from "react";

import isNil from "lodash/isNil";

import {
  AnalyticsFieldName,
  GroupSectionAnalyticsEvent,
  RecommendedGroupClassModalAnalyticsSource,
} from "@/constants/analytics/Analytics";
import {
  GroupRegistrationError,
  GroupRegistrationText,
} from "@/constants/Schedule";
import { GroupClassRegistrationModalTestID } from "@/constants/TestID";
import { useToast } from "@/hooks/useToast";
import { useAnalytics } from "@/services/Analytics";
import useAppMonitor from "@/services/AppMonitor";
import {
  useGetGroupDetailsQuery,
  useUpdateGroupSectionRegistrationMutation,
} from "@/store/api/schedule/groupClasses";
import RecommendedGroupDismissLink from "@/ui/components/groupClasses/series/RecommendedGroupDismissLink";
import GroupClassSessionList from "@/ui/components/groupClasses/series/session/GroupClassSessionList";
import { ActionBar, Button, FAIconName, Spinner } from "@/ui/core";
import { VStack } from "@/ui/layout";
import { toGroupSessions } from "@/utils/groupClassV2.util";
import { joinTestIDs } from "@/utils/string.util";

interface RecommendedGroupSectionsViewProps extends ViewWithTestID {
  dismissIcon: FAIconName;
  dismissText: string;
  groupId: string;
  onDismiss: () => void;
  onRegistered?: () => void;
  // the patient the recommendation is for — used to fetch the group's sections
  patientId?: string;
  // the logged-in user who is actually registering: a patient registers themselves,
  // a support registers themselves (recommendations are still sourced per-patient)
  registrantId?: string;
}

// v2 recommended-group modal body: fetches the group's sections (with hosts + session
// counts), lets the user select one, and registers it via the bottom "Register for group"
// button — mirroring the existing (v1) select-then-register interaction. On success it
// advances the recommendation queue via onRegistered.
const RecommendedGroupSectionsView = ({
  dismissIcon,
  dismissText,
  groupId,
  onDismiss,
  onRegistered,
  patientId,
  registrantId,
  testID,
}: RecommendedGroupSectionsViewProps) => {
  const { showToast } = useToast();
  const { captureException } = useAppMonitor();
  const { track } = useAnalytics();
  const { data: group, isLoading } = useGetGroupDetailsQuery({
    groupId,
    patientExternalId: patientId,
  });
  const [updateGroupSectionRegistration, { isLoading: isRegistering }] =
    useUpdateGroupSectionRegistrationMutation();

  const [selectedSessionId, setSelectedSessionId] = useState<string>();
  const [hasError, setHasError] = useState(false);

  if (isLoading || isNil(group)) {
    return <Spinner />;
  }

  const sessions = toGroupSessions(group);

  const handleRegister = () => {
    // register the logged-in user (patient → self, support → self), not the patient the
    // recommendation was sourced from; bail without an id since registration is user-scoped
    if (isNil(selectedSessionId) || isNil(registrantId) || isRegistering)
      return;
    setHasError(false);
    void updateGroupSectionRegistration({
      groupSectionId: selectedSessionId,
      isRegistered: true,
      patientExternalId: registrantId,
    })
      .unwrap()
      .then(() => {
        // registration here happens outside useGroupRegistrationFlow, so this view reports
        // the v2 success event itself, under the source the v1 "Register pressed" used
        track(GroupSectionAnalyticsEvent.RegisterSucceeded, {
          [AnalyticsFieldName.GroupClassId]: groupId,
          [AnalyticsFieldName.GroupSectionId]: selectedSessionId,
          [AnalyticsFieldName.IsSwitchingSection]: false,
          [AnalyticsFieldName.PatientId]: registrantId,
          [AnalyticsFieldName.Source]:
            RecommendedGroupClassModalAnalyticsSource,
        });
        showToast({
          text: GroupRegistrationText.Registered,
          variant: "success",
        });
        onRegistered?.();
      })
      .catch((error) => {
        captureException(
          new Error(GroupRegistrationError.RegisterError, { cause: error }),
          {
            context: {
              groupId,
              groupSectionId: selectedSessionId,
              patientId,
              registrantId,
            },
          },
        );
        setHasError(true);
      });
  };

  return (
    <VStack gap={24} width="100%">
      <GroupClassSessionList
        description={GroupRegistrationText.AvailableSessionsDescription}
        onSelect={setSelectedSessionId}
        selectedId={selectedSessionId}
        sessions={sessions}
        shouldHideChevron
        title={GroupRegistrationText.AvailableSessionsTitle}
      />

      <VStack gap={12} width="100%">
        {hasError && (
          <ActionBar
            iconName={FAIconName.ExclamationCircle}
            message={GroupRegistrationText.RegistrationErrorMessage}
            size="small"
            testID={joinTestIDs(
              testID,
              GroupClassRegistrationModalTestID.errorBanner,
            )}
            variant="critical"
          />
        )}
        <Button
          isDisabled={isNil(selectedSessionId) || isRegistering}
          isFullWidth
          isLoading={isRegistering}
          onPress={handleRegister}
          testID={joinTestIDs(
            testID,
            GroupClassRegistrationModalTestID.registerButton,
          )}
          title={GroupRegistrationText.RegisterCta}
          variant="primary"
        />
        <RecommendedGroupDismissLink
          dismissIcon={dismissIcon}
          dismissText={dismissText}
          onDismiss={onDismiss}
          testID={testID}
        />
      </VStack>
    </VStack>
  );
};

export default RecommendedGroupSectionsView;
